import React, { useEffect, useRef } from "react";
import useCsvImport, { STEPS } from "./useCsvImport";
import { IMPORT_FIELDS } from "../constants";
import { classNames } from "../workspace/utils";

const STEP_LABELS = {
  upload: "Upload",
  columns: "Columns",
  mapping: "Mapping",
  validation: "Validation",
  review: "Review",
};

const TYPE_LABELS = {
  email: "Email",
  url: "URL",
  phone: "Phone",
  text: "Text",
};

const TYPE_TONE = {
  email: "bg-indigo-50 text-indigo-700 ring-indigo-200",
  url: "bg-sky-50 text-sky-700 ring-sky-200",
  phone: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  text: "bg-zinc-100 text-zinc-700 ring-zinc-200",
};

function StepPill({ stepKey, activeStep, isComplete, onClick, index }) {
  const isActive = stepKey === activeStep;
  return (
    <button
      onClick={onClick}
      className={classNames(
        "flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium transition-colors",
        isActive
          ? "bg-zinc-900 text-white"
          : isComplete
            ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
            : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
      )}
    >
      <span
        className={classNames(
          "flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-semibold",
          isActive
            ? "bg-white/20 text-white"
            : isComplete
              ? "bg-emerald-200 text-emerald-800"
              : "bg-zinc-300 text-zinc-700"
        )}
      >
        {isComplete && !isActive ? "✓" : index + 1}
      </span>
      {STEP_LABELS[stepKey]}
    </button>
  );
}

function formatFileSize(bytes) {
  if (!bytes) return "0 KB";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function UploadStep({ importer, onPickFile, campaignName }) {
  const fileInputRef = useRef(null);
  const { state } = importer;

  const handleChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) onPickFile(file);
  };

  const onDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    if (file && file.name.toLowerCase().endsWith(".csv")) {
      onPickFile(file);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto p-8">
        <div className="mx-auto max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">
            Step 1 · Upload
          </p>
          <h3 className="mt-2 text-2xl font-semibold tracking-tight text-zinc-900">
            Bring a lead list into {campaignName || "this campaign"}
          </h3>
          <p className="mt-2 text-sm leading-6 text-zinc-600">
            Drag and drop a CSV or choose a file. We'll detect the columns, flag data issues, and show you exactly what will happen before anything is imported.
          </p>

          <button
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            className="mt-6 flex h-48 w-full flex-col items-center justify-center rounded-2xl border-2 border-dashed border-zinc-300 bg-zinc-50 text-zinc-600 transition-colors hover:border-indigo-400 hover:bg-indigo-50/40"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-10 w-10 text-indigo-500" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            <p className="mt-3 text-sm font-medium text-zinc-900">Drop CSV here or click to choose</p>
            <p className="mt-1 text-xs text-zinc-500">
              Up to ~25,000 rows · headers required
            </p>
            {state.file ? (
              <p className="mt-3 text-xs text-zinc-600">
                {state.file.name} · {formatFileSize(state.file.size)}
              </p>
            ) : null}
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleChange}
          />

          <div className="mt-6 grid gap-3 md:grid-cols-3">
            <div className="rounded-xl border border-zinc-200 bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                Matching
              </p>
              <p className="mt-1 text-sm text-zinc-700">
                Rows are matched by email first, then by website domain.
              </p>
            </div>
            <div className="rounded-xl border border-zinc-200 bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                Updates
              </p>
              <p className="mt-1 text-sm text-zinc-700">
                Existing leads get merged. Manual stage and internal notes are preserved.
              </p>
            </div>
            <div className="rounded-xl border border-zinc-200 bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                Enrollment
              </p>
              <p className="mt-1 text-sm text-zinc-700">
                New leads auto-enroll in the current campaign and queue for the first step.
              </p>
            </div>
          </div>

          {state.error ? (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {state.error}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ColumnReviewStep({ importer }) {
  const { state, toggleHeader, selectAllHeaders, clearHeaders, setHeaderSearch } = importer;
  const headers = state.preview?.headers || [];
  const filtered = headers.filter((header) =>
    String(header).toLowerCase().includes(String(state.headerSearch || "").toLowerCase())
  );
  const sample = state.preview?.sampleRows?.[0] || {};

  return (
    <div className="grid h-full grid-cols-[1fr,360px]">
      <div className="flex flex-col overflow-hidden">
        <div className="flex items-center gap-2 border-b border-zinc-200 bg-white px-6 py-3">
          <input
            value={state.headerSearch}
            onChange={(event) => setHeaderSearch(event.target.value)}
            placeholder="Search columns"
            className="flex-1 rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
          />
          <button
            onClick={selectAllHeaders}
            className="rounded-lg border border-zinc-300 bg-white px-2.5 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
          >
            Select all
          </button>
          <button
            onClick={clearHeaders}
            className="rounded-lg border border-zinc-300 bg-white px-2.5 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
          >
            Clear
          </button>
        </div>
        <div className="flex-1 overflow-y-auto bg-zinc-50/40 px-6 py-4">
          <div className="grid gap-2">
            {filtered.map((header) => {
              const selected = state.selectedHeaders.includes(header);
              const type = state.headerTypes?.[header] || "text";
              return (
                <label
                  key={header}
                  className={classNames(
                    "flex cursor-pointer items-start gap-3 rounded-xl border bg-white px-3 py-2.5 transition-colors",
                    selected
                      ? "border-indigo-300 bg-indigo-50/40 shadow-card"
                      : "border-zinc-200 hover:border-zinc-300"
                  )}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => toggleHeader(header)}
                    className="mt-1 h-3.5 w-3.5 rounded border-zinc-300 text-indigo-600"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate text-sm font-semibold text-zinc-900">{header}</p>
                      <span
                        className={classNames(
                          "rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 ring-inset",
                          TYPE_TONE[type]
                        )}
                      >
                        {TYPE_LABELS[type]}
                      </span>
                    </div>
                    <p className="mt-1 truncate text-xs text-zinc-500">
                      Sample: {sample[header] || "—"}
                    </p>
                  </div>
                </label>
              );
            })}
            {!filtered.length ? (
              <div className="rounded-xl border border-dashed border-zinc-300 bg-white p-6 text-center text-sm text-zinc-500">
                No columns match this search.
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <aside className="flex flex-col overflow-hidden border-l border-zinc-200 bg-white">
        <div className="border-b border-zinc-100 px-5 py-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
            Snapshot
          </p>
          <div className="mt-3 space-y-2 text-sm">
            <Row label="Rows detected" value={state.preview?.counts?.rows || 0} />
            <Row label="Columns detected" value={state.preview?.headers?.length || 0} />
            <Row label="Selected columns" value={state.selectedHeaders.length} />
            <Row label="Likely updates" value={state.preview?.counts?.matched || 0} />
            <Row label="Likely new" value={state.preview?.counts?.new || 0} />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
            Guidance
          </p>
          <ul className="mt-2 space-y-2 text-xs text-zinc-600">
            <li>Keep columns you intend to use. Others are ignored.</li>
            <li>Email is required to enroll leads. The next step handles mapping.</li>
            <li>Sample values show the first preview row.</li>
          </ul>
        </div>
      </aside>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-zinc-500">{label}</span>
      <span className="font-semibold tabular-nums text-zinc-900">{value}</span>
    </div>
  );
}

function MappingStep({ importer }) {
  const { state, setMapping, resetMapping } = importer;
  const selectedHeaders = state.selectedHeaders;

  return (
    <div className="grid h-full grid-cols-[1fr,360px]">
      <div className="flex flex-col overflow-hidden">
        <div className="flex items-center justify-between border-b border-zinc-200 bg-white px-6 py-3">
          <p className="text-sm text-zinc-600">
            Map each CRM field to a column from the CSV. A column can only map to one destination.
          </p>
          <button
            onClick={resetMapping}
            className="rounded-lg border border-zinc-300 bg-white px-2.5 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
          >
            Apply suggested
          </button>
        </div>
        <div className="flex-1 overflow-y-auto bg-zinc-50/40 px-6 py-4">
          <div className="grid gap-2 md:grid-cols-2">
            {IMPORT_FIELDS.map((field) => {
              const suggested = state.suggestedMapping?.[field.key];
              const mapped = state.mapping?.[field.key];
              const isBroken = field.required && !mapped;
              return (
                <div
                  key={field.key}
                  className={classNames(
                    "rounded-xl border bg-white p-3",
                    isBroken
                      ? "border-red-200"
                      : mapped
                        ? "border-emerald-200"
                        : "border-zinc-200"
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-zinc-900">
                        {field.label}
                        {field.required ? (
                          <span className="ml-1 text-red-500">*</span>
                        ) : null}
                      </p>
                      <p className="text-xs text-zinc-500">
                        {mapped
                          ? `Mapped from ${mapped}`
                          : suggested
                            ? `Suggested: ${suggested}`
                            : "Optional · ignored when unmapped"}
                      </p>
                    </div>
                    {mapped ? (
                      <span className="rounded-md bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
                        Mapped
                      </span>
                    ) : isBroken ? (
                      <span className="rounded-md bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red-700">
                        Required
                      </span>
                    ) : null}
                  </div>
                  <select
                    value={mapped || ""}
                    onChange={(event) => setMapping(field.key, event.target.value)}
                    className="mt-2 w-full rounded-lg border border-zinc-300 bg-white px-2.5 py-1.5 text-sm text-zinc-800 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                  >
                    <option value="">Ignore</option>
                    {selectedHeaders.map((header) => (
                      <option key={`${field.key}-${header}`} value={header}>
                        {header}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <aside className="flex flex-col overflow-hidden border-l border-zinc-200 bg-white">
        <div className="border-b border-zinc-100 px-5 py-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
            Live sample
          </p>
          <p className="mt-1 text-xs text-zinc-500">
            First row mapped to CRM fields using the current assignments.
          </p>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3">
          <dl className="space-y-2 text-xs">
            {IMPORT_FIELDS.map((field) => {
              const header = state.mapping?.[field.key];
              const value = header ? state.preview?.sampleRows?.[0]?.[header] : null;
              return (
                <div
                  key={field.key}
                  className="flex items-start justify-between gap-3 rounded-lg bg-zinc-50 px-3 py-2"
                >
                  <dt className="shrink-0 font-medium text-zinc-500">{field.label}</dt>
                  <dd
                    className={classNames(
                      "truncate text-right",
                      value ? "text-zinc-800" : "text-zinc-400"
                    )}
                  >
                    {value || "—"}
                  </dd>
                </div>
              );
            })}
          </dl>
        </div>
      </aside>
    </div>
  );
}

function IssueBadge({ issues }) {
  if (!issues?.length) {
    return (
      <span className="rounded-md bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
        Ready
      </span>
    );
  }
  const hasError = issues.some((i) => i.level === "error");
  return (
    <span
      className={classNames(
        "rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
        hasError ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"
      )}
    >
      {hasError ? `${issues.filter((i) => i.level === "error").length} error` : `${issues.length} warn`}
    </span>
  );
}

function ValidationStep({ importer }) {
  const { state, validation, setExcludeErrors } = importer;
  const mapping = state.mapping;

  return (
    <div className="grid h-full grid-cols-[1fr,360px]">
      <div className="flex flex-col overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 border-b border-zinc-200 bg-white px-6 py-3">
          <p className="text-sm text-zinc-600">
            Preview of the first {validation?.counts?.sampled || 0} rows (of {validation?.counts?.total || 0}) with mapping and validation applied.
          </p>
          <label className="ml-auto inline-flex items-center gap-2 rounded-lg border border-zinc-300 bg-white px-2.5 py-1.5 text-xs font-medium text-zinc-700">
            <input
              type="checkbox"
              checked={state.excludeErrors}
              onChange={(event) => setExcludeErrors(event.target.checked)}
              className="h-3.5 w-3.5 rounded border-zinc-300 text-indigo-600"
            />
            Exclude rows with errors
          </label>
        </div>
        <div className="flex-1 overflow-y-auto bg-zinc-50/40 px-6 py-4">
          <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-card">
            <div className="grid grid-cols-[48px,1fr,1fr,1fr,auto] gap-3 border-b border-zinc-200 bg-zinc-50 px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
              <span>#</span>
              <span>Email</span>
              <span>Company</span>
              <span>Website</span>
              <span className="text-right">Status</span>
            </div>
            <div className="divide-y divide-zinc-100">
              {(validation?.rows || []).map((entry) => {
                const email = mapping.email ? entry.row?.[mapping.email] : "";
                const company = mapping.company_name
                  ? entry.row?.[mapping.company_name]
                  : "";
                const website = mapping.website ? entry.row?.[mapping.website] : "";
                return (
                  <div key={entry.index} className="grid grid-cols-[48px,1fr,1fr,1fr,auto] gap-3 px-4 py-2 text-sm">
                    <span className="text-xs text-zinc-400">{entry.index + 1}</span>
                    <span className={classNames("truncate", entry.hasError ? "text-red-600" : "text-zinc-800")}>
                      {email || <span className="text-zinc-400">—</span>}
                    </span>
                    <span className="truncate text-zinc-800">
                      {company || <span className="text-zinc-400">—</span>}
                    </span>
                    <span className="truncate text-zinc-600">
                      {website || <span className="text-zinc-400">—</span>}
                    </span>
                    <div className="flex items-center justify-end gap-1">
                      <IssueBadge issues={entry.issues} />
                    </div>
                    {entry.issues.length ? (
                      <div className="col-span-5 mt-1 flex flex-wrap gap-1">
                        {entry.issues.map((issue, idx) => (
                          <span
                            key={idx}
                            className={classNames(
                              "rounded-md px-1.5 py-0.5 text-[10px] font-medium",
                              issue.level === "error"
                                ? "bg-red-50 text-red-700"
                                : "bg-amber-50 text-amber-700"
                            )}
                          >
                            {issue.message}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <aside className="flex flex-col overflow-hidden border-l border-zinc-200 bg-white">
        <div className="border-b border-zinc-100 px-5 py-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
            Summary
          </p>
          <div className="mt-3 grid gap-2">
            <SummaryTile label="Total rows" value={validation?.counts?.total || 0} />
            <SummaryTile label="Sampled" value={validation?.counts?.sampled || 0} />
            <SummaryTile
              label="Clean"
              value={validation?.counts?.clean || 0}
              tone="success"
            />
            <SummaryTile
              label="Warnings"
              value={validation?.counts?.warnings || 0}
              tone="warning"
            />
            <SummaryTile
              label="Errors"
              value={validation?.counts?.errors || 0}
              tone="danger"
            />
          </div>
        </div>
        <div className="border-b border-zinc-100 px-5 py-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
            Plan
          </p>
          <div className="mt-3 space-y-2 text-sm">
            <Row label="Likely new" value={validation?.counts?.new || 0} />
            <Row label="Likely updates" value={validation?.counts?.matched || 0} />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
            What happens next
          </p>
          <ul className="mt-2 space-y-2 text-xs text-zinc-600">
            <li>Review the overall plan on the next step before importing.</li>
            <li>
              {state.excludeErrors
                ? "Error rows will be skipped on import."
                : "Error rows will be imported and may be rejected by the server."}
            </li>
            <li>Duplicate rows within this file are flagged as warnings.</li>
          </ul>
        </div>
      </aside>
    </div>
  );
}

function SummaryTile({ label, value, tone = "default" }) {
  const toneClass = {
    default: "text-zinc-900",
    success: "text-emerald-700",
    warning: "text-amber-700",
    danger: "text-red-700",
  }[tone];
  return (
    <div className="flex items-center justify-between rounded-lg bg-zinc-50 px-3 py-2">
      <span className="text-xs text-zinc-500">{label}</span>
      <span className={classNames("text-sm font-semibold tabular-nums", toneClass)}>
        {value}
      </span>
    </div>
  );
}

function ReviewStep({ importer, campaignName }) {
  const { state, validation } = importer;
  const mapping = state.mapping || {};
  const mappedFields = Object.entries(mapping).filter(([, v]) => v);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto p-8">
        <div className="mx-auto max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">
            Step 5 · Review & enroll
          </p>
          <h3 className="mt-2 text-2xl font-semibold tracking-tight text-zinc-900">
            Merge into {campaignName || "this campaign"}
          </h3>
          <p className="mt-2 text-sm leading-6 text-zinc-600">
            Confirm the plan below. New rows will be created and enrolled into this campaign's sequence, and existing leads will be merged without overwriting manual stage or notes.
          </p>

          <div className="mt-6 grid gap-3 md:grid-cols-4">
            <PlanTile label="Will be created" value={validation?.counts?.new || 0} tone="success" />
            <PlanTile label="Will be updated" value={validation?.counts?.matched || 0} tone="primary" />
            <PlanTile
              label={state.excludeErrors ? "Will be skipped" : "Will be attempted"}
              value={validation?.counts?.errors || 0}
              tone={state.excludeErrors ? "warning" : "danger"}
            />
            <PlanTile label="Clean rows" value={validation?.counts?.clean || 0} />
          </div>

          <div className="mt-6 rounded-2xl border border-zinc-200 bg-white p-4 shadow-card">
            <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
              Mapping summary ({mappedFields.length} fields)
            </p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {mappedFields.map(([field, header]) => {
                const label = IMPORT_FIELDS.find((f) => f.key === field)?.label || field;
                return (
                  <div
                    key={field}
                    className="flex items-center justify-between rounded-lg bg-zinc-50 px-3 py-2 text-sm"
                  >
                    <span className="text-zinc-500">{label}</span>
                    <span className="truncate text-right font-medium text-zinc-800">{header}</span>
                  </div>
                );
              })}
              {!mappedFields.length ? (
                <p className="col-span-2 text-sm text-zinc-500">No fields mapped yet.</p>
              ) : null}
            </div>
          </div>

          <div className="mt-4 rounded-2xl border border-zinc-200 bg-white p-4 shadow-card">
            <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
              Settings
            </p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2 text-sm">
              <div className="flex justify-between rounded-lg bg-zinc-50 px-3 py-2">
                <span className="text-zinc-500">Source file</span>
                <span className="truncate font-medium text-zinc-800">
                  {state.file?.name || "—"}
                </span>
              </div>
              <div className="flex justify-between rounded-lg bg-zinc-50 px-3 py-2">
                <span className="text-zinc-500">Columns selected</span>
                <span className="font-medium text-zinc-800">
                  {state.selectedHeaders.length}
                </span>
              </div>
              <div className="flex justify-between rounded-lg bg-zinc-50 px-3 py-2">
                <span className="text-zinc-500">Skip error rows</span>
                <span className="font-medium text-zinc-800">
                  {state.excludeErrors ? "Yes" : "No"}
                </span>
              </div>
              <div className="flex justify-between rounded-lg bg-zinc-50 px-3 py-2">
                <span className="text-zinc-500">Enroll new rows</span>
                <span className="font-medium text-zinc-800">Yes · step 1</span>
              </div>
            </div>
          </div>

          {state.error ? (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {state.error}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function PlanTile({ label, value, tone = "default" }) {
  const toneClass = {
    default: "bg-white text-zinc-900",
    success: "bg-emerald-50 text-emerald-800 border-emerald-200",
    primary: "bg-indigo-50 text-indigo-800 border-indigo-200",
    warning: "bg-amber-50 text-amber-800 border-amber-200",
    danger: "bg-red-50 text-red-800 border-red-200",
  }[tone] || "bg-white text-zinc-900";
  return (
    <div className={classNames("rounded-xl border border-zinc-200 p-4", toneClass)}>
      <p className="text-xs font-medium uppercase tracking-wider">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums">{value}</p>
    </div>
  );
}

export default function CsvImportWizard({
  isOpen,
  onClose,
  activeCampaignId,
  campaignName,
  lastMapping,
  onImported,
}) {
  const importer = useCsvImport({ activeCampaignId, lastMapping });
  const { state, goToStep, previewFile, canAdvance, commit, reset } = importer;
  const currentIndex = STEPS.indexOf(state.step);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const advance = () => {
    const index = STEPS.indexOf(state.step);
    const nextStep = STEPS[index + 1];
    if (!nextStep) return;
    if (!canAdvance(state.step)) return;
    goToStep(nextStep);
  };

  const retreat = () => {
    const index = STEPS.indexOf(state.step);
    const prev = STEPS[index - 1];
    if (prev) goToStep(prev);
  };

  const onCommit = async () => {
    await commit((result) => {
      onImported?.(result);
      onClose?.();
    });
  };

  const stepContent = (() => {
    if (state.step === "upload") {
      return (
        <UploadStep
          importer={importer}
          onPickFile={previewFile}
          campaignName={campaignName}
        />
      );
    }
    if (state.loading) {
      return (
        <div className="flex h-full items-center justify-center text-sm text-zinc-500">
          Preparing preview…
        </div>
      );
    }
    if (state.step === "columns") return <ColumnReviewStep importer={importer} />;
    if (state.step === "mapping") return <MappingStep importer={importer} />;
    if (state.step === "validation") return <ValidationStep importer={importer} />;
    if (state.step === "review") return <ReviewStep importer={importer} campaignName={campaignName} />;
    return null;
  })();

  const primaryLabel = state.step === "review"
    ? state.committing
      ? "Importing…"
      : `Import into ${campaignName || "campaign"}`
    : "Continue";

  return (
    <div className="fixed inset-0 z-[80] flex items-stretch justify-center bg-zinc-900/40 backdrop-blur-sm p-4 md:p-6">
      <div className="flex w-full max-w-[1280px] flex-col overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-panel">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200 px-6 py-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-600">
              CSV import
            </p>
            <h2 className="text-base font-semibold text-zinc-900">
              {campaignName ? `Merge list into ${campaignName}` : "Merge list"}
            </h2>
          </div>
          <div className="flex flex-1 items-center justify-center gap-1.5">
            {STEPS.map((step, index) => (
              <StepPill
                key={step}
                stepKey={step}
                activeStep={state.step}
                isComplete={index < currentIndex}
                onClick={() => (index < currentIndex ? goToStep(step) : null)}
                index={index}
              />
            ))}
          </div>
          <button
            onClick={onClose}
            className="rounded-md px-2.5 py-1.5 text-sm font-medium text-zinc-600 hover:bg-zinc-100"
          >
            Close
          </button>
        </header>

        <div className="flex-1 overflow-hidden bg-white">{stepContent}</div>

        <footer className="flex items-center justify-between border-t border-zinc-200 bg-white px-6 py-3">
          <div className="text-xs text-zinc-500">
            {state.step === "upload"
              ? "Choose a CSV to begin."
              : state.step === "columns"
                ? `${state.selectedHeaders.length} columns selected.`
                : state.step === "mapping"
                  ? `${importer.mappedFieldCount} fields mapped.`
                  : state.step === "validation"
                    ? `${importer.validation?.counts?.errors || 0} errors · ${importer.validation?.counts?.warnings || 0} warnings.`
                    : `${importer.validation?.counts?.new || 0} new · ${importer.validation?.counts?.matched || 0} updates.`}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={retreat}
              disabled={state.step === "upload"}
              className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:text-zinc-300"
            >
              Back
            </button>
            {state.step === "review" ? (
              <button
                onClick={onCommit}
                disabled={state.committing || !state.preview}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-zinc-300"
              >
                {primaryLabel}
              </button>
            ) : (
              <button
                onClick={advance}
                disabled={state.loading || !canAdvance(state.step)}
                className="rounded-lg bg-zinc-900 px-4 py-2 text-sm font-medium text-white hover:bg-zinc-800 disabled:cursor-not-allowed disabled:bg-zinc-300"
              >
                {primaryLabel}
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}
